import { AxiosResponse } from 'axios'
import BaseTableCard from 'common/components/BaseTableCard'
import BasicTable from 'common/components/BasicTable'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { FeatureKey } from 'common/constants/permission'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { parseISO } from 'date-fns'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import MoreAction from 'modules/pond/components/MoreAction'
import { SpeciesName } from 'modules/pond/constants'
import { useMutateDeleteShrimpSales } from 'modules/shrimpSales/api/mutation'
import { useGetShrimpSales, useGetShrimpSalesTypes } from 'modules/shrimpSales/api/queries'
import { ShrimpSales } from 'modules/shrimpSales/api/types'
import { shrimpSalesDateTableFilterState } from 'modules/shrimpSales/stores/atom'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { normalizeSpeciesName } from 'utils/analytics'

import { Avatar, Box, Dialog, IconButton } from '@mui/material'

import ShrimpSalesDetailDialog from '../ShrimpSalesDetailDialog'
import ShrimpSalesRecordDialog from '../ShrimpSalesRecordDialog'

type ShrimpSalesRow = {
  id: number
  date: string
  buyer: string
  shrimpType?: string | null
  shrimpPerKg: number
  pieceCount: number | null
  averagePrice: number | null
  amount: number | null
  note?: string | null
  photo: React.ReactNode
  actions: React.ReactNode
}

const ShrimpSalesSummary = () => {
  const { t } = useTranslation(['shrimpSales', 'common'])

  const { data, refetch, isFetching } = useGetShrimpSales()
  const deleteMutation = useMutateDeleteShrimpSales()
  const [deletingId, setDeletingId] = useState(-1)
  const speciesName = useSpeciesName()
  const isFish = useIsFishSpecie()
  const { trackEvent } = useAnalytics()
  const [open, setOpen] = useState(false)
  const { data: shrimpSalesTypes } = useGetShrimpSalesTypes()
  const getShrimpSalesTypeName = (id: number) => shrimpSalesTypes?.find((el) => el.id === id)?.type
  const [selectedSale, setSelectedSale] = useState<ShrimpSales | undefined>(undefined)
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailSale, setDetailSale] = useState<ShrimpSales | undefined>(undefined)

  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      await deleteMutation.mutateAsync(deletingId, {
        onSuccess: (res: AxiosResponse<any>) => {
          const { status } = res.data
          if (status !== 200) return
          const normalizedSpecies = normalizeSpeciesName(speciesName)
          trackEvent(
            AnalyticsEvents.salesRecordDeleted,
            normalizedSpecies ? { species: normalizedSpecies } : undefined
          )
        },
      })
    },
  })

  const handleOpenDeleteConfirm = (id: number) => {
    setDeletingId(id)
    openConfirmDialog()
  }

  const handleOpenPreview = (src: string) => {
    setPreviewSrc(src)
  }

  const handleClosePreview = () => {
    setPreviewSrc(null)
  }

  const columns = [
    {
      id: 'date',
      title: t('date.add'),
      width: '180px',
      format: (value: string) => formatThaiShortDateTwoDigitYear(parseISO(value)),
    },
    {
      id: 'buyer',
      title: <Trans i18nKey={'buyer'} ns="shrimpSales" values={{ species: speciesName }} />,
      width: '160px',
    },
    {
      id: 'shrimpType',
      title: t('shrimp.type'),
      width: '150px',
    },
    {
      id: 'shrimpPerKg',
      title:
        speciesName === SpeciesName.Fish ? t('fish.size.gram.per.one') : t('shrimp.size.per.kg'),
      width: '150px',
      format: (v: number) => fNumber(v),
    },
    // "จำนวนตัว" มาจาก backend ตรง ๆ (DOF-1313/1314) — ห้ามคำนวณเองจากน้ำหนัก×ไซส์
    // เพราะ backend ปัดเศษต่อแถว subcategory ก่อนรวม โชว์เฉพาะบ่อปลาตามที่ตกลงกัน
    ...(isFish
      ? [
        {
          id: 'pieceCount',
          title: t('piece.count'),
          width: '130px',
          format: (v: number | null) => (v != null ? fNumber(v, { disabledDecimal: true }) : '-'),
        },
      ]
      : []),
    {
      id: 'amount',
      title: t('total.weight.kg'),
      width: '150px',
      format: (v: number | null) => (v != null ? fNumber(v) : '-'),
    },
    {
      id: 'averagePrice',
      title: t('price'),
      width: '150px',
      format: (v: number | null) => (v != null ? fNumber(v) : '-'),
    },
    {
      id: 'note',
      title: t('note'),
      width: '200px',
    },
    {
      id: 'photo',
      title: t('photo'),
      width: '120px',
      preventRowClick: true,
    },
    {
      id: 'actions',
      title: '',
      width: '80px',
      preventRowClick: true,
    },
  ]

  const handleOpenEdit = (row: ShrimpSales) => {
    setSelectedSale(row)
    setOpen(true)
  }

  const handleCloseEdit = () => {
    setOpen(false)
  }

  const handleOpenDetail = (row: ShrimpSalesRow) => {
    const sale = (data ?? []).find((el) => el.id === row.id)
    if (!sale) return
    setDetailSale(sale)
    setDetailOpen(true)
  }

  const handleCloseDetail = () => {
    setDetailOpen(false)
  }

  const rows: ShrimpSalesRow[] = (data ?? []).map((sale) => {
    return {
      id: sale.id,
      date: sale.date,
      buyer: sale.buyerName,
      shrimpType: getShrimpSalesTypeName(sale.shrimpSalesTypeId),
      shrimpPerKg: sale.shrimpCountPerKg,
      pieceCount: sale.totalQuantityCount,
      averagePrice: sale.totalRevenue,
      amount: sale.totalQuantityInKg,
      note: sale.note ? sale.note : '-',
      photo: sale.billPhotoURL ? (
        <Box display="flex" justifyContent="center">
          <Box sx={{ cursor: 'pointer' }} onClick={() => handleOpenPreview(sale.billPhotoURL!)}>
            <Avatar
              src={sale.billPhotoURL || undefined}
              alt={t('view.photo')}
              sx={{ width: 40, height: 40 }}
            />
          </Box>
        </Box>
      ) : (
        <Box>-</Box>
      ),
      actions: (
        <MoreAction
          featureKey={FeatureKey.Sales}
          isHideOnCompleted={false}
          onClickEdit={() => handleOpenEdit(sale)}
          onClickDelete={() => handleOpenDeleteConfirm(sale.id)}
        />
      ),
    }
  })

  return (
    <>
      {dialog}
      <Box mt={4}>
        <BaseTableCard
          disableRefresh={true}
          title={t('sumary.shrimp.sale', { speciesName })}
          refresh={refetch}
          isLoading={isFetching}
          isEmpty={data?.length === 0}
          dateRecoilState={shrimpSalesDateTableFilterState}
          disableShowDateFilter={false}
          enableDateRangeFilter={false}
        >
          <Box className="setting-table">
            <BasicTable
              columns={columns}
              onLoading={() => { }}
              isLoading={isFetching}
              rows={rows}
              onRowClick={handleOpenDetail}
            />
          </Box>
        </BaseTableCard>
        <ShrimpSalesRecordDialog open={open} onClose={handleCloseEdit} data={selectedSale} />
        <ShrimpSalesDetailDialog
          open={detailOpen}
          onClose={handleCloseDetail}
          data={detailSale}
          onClickEdit={() => {
            if (detailSale) handleOpenEdit(detailSale)
            handleCloseDetail()
          }}
          onClickDelete={() => {
            if (detailSale) handleOpenDeleteConfirm(detailSale.id)
            handleCloseDetail()
          }}
          onClickPhoto={(src) => {
            handleOpenPreview(src)
            handleCloseDetail()
          }}
        />
      </Box>
      <Dialog open={!!previewSrc} onClose={handleClosePreview} maxWidth="lg">
        <IconButton
          onClick={handleClosePreview}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.common.white,
            zIndex: 2,
          }}
        >
          ✕
        </IconButton>

        <Box>
          <img
            src={previewSrc || ''}
            alt="preview"
            style={{
              maxWidth: '90vw',
              maxHeight: '80vh',
              borderRadius: 8,
            }}
          />
        </Box>
      </Dialog>
    </>
  )
}

export default ShrimpSalesSummary
